import { regex } from '../../../utils/helper';
import { translate } from '../../../utils/translations';

// Same limits as the existing login validations.
const EMAIL_MAX_LENGTH = 50;
const PASSWORD_MAX_LENGTH = 20;
const OTP_LENGTH = 6;
// Same character rule as registration (regex.contractIdRegExp: letters, digits, hyphens), capped at 10 here.
const CONTRACT_ID_MAX_LENGTH = 10;

const digitsOnly = (value: string): string => value.replace(/\D/g, '');

/**
 * Strips formatting and the US country code, returning at most 10 national digits.
 * NANP area codes never start with 1, so a leading 1 (as in "+1" or "1 555...") is always the country code.
 */
export const extractPhoneDigits = (value: string): string => {
    const digits = digitsOnly(value);
    const national = digits.startsWith('1') ? digits.slice(1) : digits;
    return national.slice(0, 10);
};

/** Live-formats national digits as "(XXX) XXX-XXXX". */
export const formatPhoneForDisplay = (digits: string): string => {
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const normalizeEmail = (value: string): string => value.trim().toLowerCase();

export const validateEmail = (value: string): string | null => {
    const email = value.trim();
    if (!email) return translate('authFlow.validation.emailRequired');
    if (!regex.emailRegExp.test(email)) return translate('authFlow.validation.emailInvalid');
    return null;
};

export const validatePhone = (digits: string): string | null => {
    if (!digits) return translate('authFlow.validation.phoneRequired');
    if (!regex.mobileRegExp.test(digits)) return translate('authFlow.validation.phoneLength');
    return null;
};

export const validateContractId = (value: string): string | null => {
    const id = value.trim();
    if (!id) return translate('authFlow.validation.contractRequired');
    return id.length <= CONTRACT_ID_MAX_LENGTH && regex.contractIdRegExp.test(id)
        ? null
        : translate('authFlow.validation.contractInvalid');
};

// Never trimmed or altered; same password rule as the existing login validation.
export const validatePassword = (value: string): string | null => {
    if (value.length === 0) return translate('authFlow.validation.passwordRequired');
    return regex.passwordRegExp.test(value) ? null : translate('authFlow.validation.passwordInvalid');
};

export const sanitizeOtp = (value: string): string => digitsOnly(value).slice(0, OTP_LENGTH);

export const validateOtp = (value: string): string | null =>
    value.length === OTP_LENGTH ? null : translate('authFlow.validation.otpIncomplete');

export const LOGIN_LIMITS = { EMAIL_MAX_LENGTH, PASSWORD_MAX_LENGTH, OTP_LENGTH, CONTRACT_ID_MAX_LENGTH };
